import React, { useState } from 'react';

interface PreOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  basePrice: number;
  returnPath?: string;
}

const PROMO_NAME = 'JACKSON';
const PROMO_DISCOUNT_PCT = 0.10;

const PreOrderModal: React.FC<PreOrderModalProps> = ({ isOpen, onClose, basePrice, returnPath }) => {
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [loading, setLoading] = useState(false);

  const effectivePrice = Number(
    (promoApplied ? basePrice * (1 - PROMO_DISCOUNT_PCT) : basePrice).toFixed(2)
  );

  const applyPromo = () => {
    const code = promoCode.trim().toUpperCase();
    if (!code) return setPromoError('Enter a code.');
    if (code === PROMO_NAME) {
      setPromoApplied(true);
      setPromoError('');
    } else {
      setPromoApplied(false);
      setPromoError('Invalid code.');
    }
  };

  const clearPromo = () => {
    setPromoApplied(false);
    setPromoCode('');
    setPromoError('');
  };

  const startCheckout = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const resp = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: effectivePrice,
          promoCode: promoApplied ? PROMO_NAME : '',
          basePrice,
          cancelPath: returnPath || '/',
        }),
      });

      const contentType = resp.headers.get('content-type') || '';
      if (!resp.ok) {
        const msg = contentType.includes('application/json')
          ? (await resp.json())?.error?.message
          : (await resp.text());
        throw new Error(msg || `HTTP ${resp.status}`);
      }

      const data: { url?: string } = await resp.json();
      if (!data?.url) throw new Error('No checkout URL returned from server.');

      window.location.href = data.url;
    } catch (e: any) {
      console.error('Error creating checkout session:', e);
      alert(`Failed to start checkout: ${e?.message || e}`);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 md:p-8 relative">
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close pre-order modal"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Order Goldilocks Smart Solutions
          </h2>

          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 mb-4">
            <p className="text-gray-700 mb-2">Regular Price</p>
            <p className="text-3xl font-bold text-[#213B5C]">${basePrice.toFixed(2)}</p>
            <p className="text-sm text-gray-600 mt-1">Free shipping included</p>
          </div>

          {/* Promo code */}
          <div className="mb-4">
            <div className="max-w-sm mx-auto flex gap-2">
              <input
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
                placeholder="Enter promo code"
                className="flex-1 rounded-full border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#213B5C]"
              />
              {!promoApplied ? (
                <button
                  onClick={applyPromo}
                  className="rounded-full bg-[#213B5C] text-white px-4 py-2 font-semibold hover:bg-[#1a2f4a]"
                >
                  Apply
                </button>
              ) : (
                <button
                  onClick={clearPromo}
                  className="rounded-full border border-[#213B5C] text-[#213B5C] px-4 py-2 font-semibold hover:bg-[#213B5C] hover:text-white"
                >
                  Remove
                </button>
              )}
            </div>
            {promoError && <p className="mt-2 text-sm text-red-600">{promoError}</p>}
            {promoApplied && (
              <div className="mt-2 text-sm text-gray-700">
                Was ${basePrice.toFixed(2)} → Now <strong>${effectivePrice.toFixed(2)}</strong> (10% off)
              </div>
            )}
          </div>

          <button
            onClick={startCheckout}
            disabled={loading}
            className={`w-full bg-[#213B5C] text-white px-6 py-3 rounded-full font-semibold transition-all ${
              loading ? 'opacity-60 cursor-not-allowed' : 'hover:bg-[#1a2f4a]'
            }`}
          >
            {loading ? 'Starting checkout…' : `Reserve Now ($${effectivePrice.toFixed(2)})`}
          </button>

          <p className="mt-2 text-xs text-gray-500">
            Secure payment via Stripe. You'll be redirected to a hosted checkout page.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PreOrderModal;
